"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, Plus, ArrowUp, ArrowDown, Pencil, Trash, PlusCircle } from "lucide-react";
import AddSupplierModal from '../components/AddSupplierModal';
import EditSupplierModal from '../components/EditSupplierModal';
import { api } from '../utils/api';

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [transactionError, setTransactionError] = useState("");
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split("T")[0],
    supplierId: "",
    partyName: "",
    type: "credit",
    amount: "",
    description: "",
    paymentMode: "Cash",
  });
  const navigate = useNavigate();
  const { storeId } = useParams();

  useEffect(() => {
    if (storeId) {
      fetchSuppliers();
    } else {
      setError("No store ID provided");
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [page, size, search, sortBy, sortDir, storeId]);

  const fetchSuppliers = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching suppliers with token:', token ? 'Token present' : 'No token');
      const res = await api.get(`/suppliers/by-store/${storeId}?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}&search=${search}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      console.log('Suppliers response:', res.data);
      console.log('Suppliers response.data.data.content:', res.data.data?.content);
      const mappedSuppliers = Array.isArray(res.data.data?.content)
        ? res.data.data.content.map(supplier => ({
            ...supplier,
            type: "supplier",
            email: supplier.email || "",
            lastTransaction: supplier.updatedAt || null,
            balance: supplier.currentBalance ?? 0,
          }))
        : [];
      console.log('Mapped suppliers:', mappedSuppliers);
      setSuppliers(mappedSuppliers);
      setTotalPages(res.data.data?.totalPages || 1);
    } catch (err) {
      console.error('Fetch suppliers error:', err);
      if (err.response?.status === 401) {
        console.log('Unauthorized - redirecting to login');
        navigate('/login');
      }
      setError(err.response?.data?.message || err.message || 'Failed to fetch suppliers');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/suppliers/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      setSuppliers(suppliers.filter(p => p.id !== id));
      await fetchSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete supplier');
    }
  };

  const handleEditSupplier = (updatedSupplier) => {
    setSuppliers(suppliers.map(p => p.id === updatedSupplier.id ? {
      ...updatedSupplier,
      type: "supplier",
      email: updatedSupplier.email || "",
      lastTransaction: updatedSupplier.updatedAt || null,
      balance: updatedSupplier.currentBalance ?? 0,
    } : p));
    setShowEditModal(false);
    setSelectedSupplier(null);
  };

  const handleAddSupplier = async (newSupplier) => {
    try {
      setError("");
      await fetchSuppliers();
      setShowAddSupplierModal(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to add supplier');
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      await api.post(`/suppliers/by-store/${storeId}/import`, {}, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      await fetchSuppliers();
      alert('Import successful!');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to import suppliers');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/suppliers/by-store/${storeId}/export`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `suppliers_store_${storeId}_export.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert('Export successful!');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to export suppliers');
    } finally {
      setExporting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTransaction({
      ...newTransaction,
      [name]: name === "amount" ? (value === "" ? "" : Number(value)) : value,
    });
  };

  const handleAddTransaction = async () => {
    if (!selectedSupplier?.id) {
      setTransactionError("Please select a supplier");
      return;
    }
    if (!newTransaction.amount || newTransaction.amount <= 0) {
      setTransactionError("Amount must be greater than 0");
      return;
    }
    if (!newTransaction.description.trim()) {
      setTransactionError("Description is required");
      return;
    }
    if (!newTransaction.date) {
      setTransactionError("Date is required");
      return;
    }

    setTransactionError("");
    try {
      const token = localStorage.getItem('token');
      const payload = {
        supplierId: selectedSupplier.id,
        type: newTransaction.type === "credit" ? "PAYMENT_RECEIVED" : "PAYMENT_GIVEN",
        amount: Math.abs(newTransaction.amount),
        transactionDate: newTransaction.date,
        mode: newTransaction.paymentMode.toUpperCase().replace(" ", "_"),
        category: newTransaction.description,
        notes: newTransaction.description,
        receiptNumber: `TRX-${Date.now()}`,
      };
      console.log('Transaction payload:', payload);
      console.log('Current balance before transaction:', selectedSupplier.balance);
      const res = await api.post(`/suppliers/by-store/${storeId}/transactions/supplier/payment`, payload, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      console.log('Transaction response:', res.data);
      await fetchSuppliers();
      setShowAddModal(false);
      setNewTransaction({
        date: new Date().toISOString().split("T")[0],
        supplierId: "",
        partyName: "",
        type: "credit",
        amount: "",
        description: "",
        paymentMode: "Cash",
      });
      setSelectedSupplier(null);
      alert("Transaction added successfully!");
    } catch (err) {
      console.error('Transaction error:', err);
      setTransactionError(err.response?.data?.message || err.message || 'Failed to add transaction');
    }
  };

  const filteredSuppliers = suppliers.filter((supplier) => {
    const matchesSearch = supplier.name?.toLowerCase().includes(search.toLowerCase()) || false;
    const matchesBalance = balanceFilter === "all" || 
      ((supplier.balance ?? 0) > 0 && balanceFilter === "positive") || 
      ((supplier.balance ?? 0) < 0 && balanceFilter === "negative") || 
      ((supplier.balance ?? 0) === 0 && balanceFilter === "zero");
    return matchesSearch && matchesBalance;
  });

  const totalReceivable = filteredSuppliers
    .filter((supplier) => (supplier.balance ?? 0) > 0)
    .reduce((sum, supplier) => sum + (supplier.balance ?? 0), 0);

  const totalPayable = filteredSuppliers
    .filter((supplier) => (supplier.balance ?? 0) < 0)
    .reduce((sum, supplier) => sum + Math.abs(supplier.balance ?? 0), 0);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Suppliers</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddSupplierModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4 mr-2 inline" />
            Add Supplier
          </button>
          <button
            onClick={handleImport}
            disabled={importing}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            {importing ? 'Importing...' : 'Import'}
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
          >
            {exporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="pl-10 block w-full md:w-64 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        <select
          value={balanceFilter}
          onChange={(e) => setBalanceFilter(e.target.value)}
          className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="all">All Balances</option>
          <option value="positive">Receivable</option>
          <option value="negative">Payable</option>
          <option value="zero">Zero Balance</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); setPage(0); }}
          className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="name">Sort by Name</option>
          <option value="currentBalance">Sort by Balance</option>
          <option value="gstNumber">Sort by GST Number</option>
        </select>

        <select
          value={sortDir}
          onChange={(e) => { setSortDir(e.target.value); setPage(0); }}
          className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>

      {error && <div className="mb-4 text-red-600">{error}</div>}
      {transactionError && <div className="mb-4 text-red-600">{transactionError}</div>}
      {loading ? <div className="text-gray-600">Loading...</div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Suppliers</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredSuppliers.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Stock Received</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    ₹{totalReceivable.toLocaleString()}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <ArrowDown className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                From {filteredSuppliers.filter((supplier) => (supplier.balance ?? 0) > 0).length} suppliers
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Pending</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">₹{totalPayable.toLocaleString()}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <ArrowUp className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                To {filteredSuppliers.filter((supplier) => (supplier.balance ?? 0) < 0).length} suppliers
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">GST Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Transaction</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td 
                        className="px-6 py-4 whitespace-nowrap cursor-pointer" 
                        onClick={() => {
                          setSelectedSupplier(supplier);
                          setNewTransaction({ ...newTransaction, supplierId: supplier.id, partyName: supplier.name || 'Unknown' });
                          setShowAddModal(true);
                        }}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-300">{supplier.name || 'Unknown'}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{supplier.address || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {supplier.gstNumber || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-300">{supplier.phone || '-'}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{supplier.email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <span className={`font-medium ${
                          (supplier.balance ?? 0) > 0 ? 'text-green-600' : (supplier.balance ?? 0) < 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-300'
                        }`}>
                          ₹{(supplier.balance ?? 0).toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {supplier.lastTransaction ? new Date(supplier.lastTransaction).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <button 
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-3"
                          onClick={() => { setSelectedSupplier(supplier); setShowEditModal(true); }}
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                        <button 
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 mr-3"
                          onClick={() => { setSelectedSupplier(supplier); setShowDeleteModal(true); }}
                        >
                          <Trash className="h-5 w-5" />
                        </button>
                        <button 
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          onClick={() => { 
                            setSelectedSupplier(supplier); 
                            setNewTransaction({ ...newTransaction, supplierId: supplier.id, partyName: supplier.name || 'Unknown' }); 
                            setShowAddModal(true); 
                          }}
                        >
                          <PlusCircle className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredSuppliers.length === 0 && !loading && !error && (
            <div className="text-center mt-6 text-gray-500 dark:text-gray-400">
              No suppliers found for this store.
            </div>
          )}

          <div className="flex justify-center mt-4 gap-2">
            <button 
              disabled={page === 0} 
              onClick={() => setPage(page - 1)} 
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <span>Page {page + 1} of {totalPages}</span>
            <button 
              disabled={page + 1 >= totalPages} 
              onClick={() => setPage(page + 1)} 
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              ​
            </span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Add New Transaction</h3>
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Date
                          </label>
                          <input
                            type="date"
                            name="date"
                            id="date"
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            value={newTransaction.date}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Type
                          </label>
                          <select
                            name="type"
                            id="type"
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            value={newTransaction.type}
                            onChange={handleInputChange}
                          >
                            <option value="credit">Credit (Receive)</option>
                            <option value="debit">Debit (Pay)</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="partyName"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Supplier Name
                        </label>
                        <input
                          type="text"
                          name="partyName"
                          id="partyName"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          value={newTransaction.partyName}
                          disabled
                        />
                      </div>

                      <div>
                        <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Amount
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 dark:text-gray-400 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            name="amount"
                            id="amount"
                            className="block w-full pl-7 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            value={newTransaction.amount}
                            onChange={handleInputChange}
                            min="0.01"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="description"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Description
                        </label>
                        <input
                          type="text"
                          name="description"
                          id="description"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          value={newTransaction.description}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="paymentMode"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Payment Mode
                        </label>
                        <select
                          name="paymentMode"
                          id="paymentMode"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          value={newTransaction.paymentMode}
                          onChange={handleInputChange}
                        >
                          <option value="Cash">Cash</option>
                          <option value="UPI">UPI</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Cheque">Cheque</option>
                          <option value="Credit Card">Credit Card</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleAddTransaction}
                >
                  Add Transaction
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => { setShowAddModal(false); setSelectedSupplier(null); setTransactionError(""); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedSupplier && (
        <EditSupplierModal
          open={showEditModal}
          onClose={() => { setShowEditModal(false); setSelectedSupplier(null); }}
          supplier={selectedSupplier}
          onEditSupplier={handleEditSupplier}
        />
      )}

      {showDeleteModal && selectedSupplier && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"></span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-red-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                      Delete Supplier
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete {selectedSupplier?.name || 'Unknown'}? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    handleDelete(selectedSupplier.id);
                    setShowDeleteModal(false);
                    setSelectedSupplier(null);
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedSupplier(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddSupplierModal && (
        <AddSupplierModal 
          open={showAddSupplierModal} 
          onClose={() => setShowAddSupplierModal(false)} 
          onAddSupplier={handleAddSupplier}
        />
      )}
    </div>
  );
}